/**
 * Locating product-tour targets in the DOM.
 *
 * Split out from both the tour registry and `useProductTour` because both need
 * it — the composable waits for a step's own anchor, and a step's `prepare`
 * hook waits for whatever it has to click to get there. Importing the
 * composable from the registry would be a cycle.
 *
 * driver.js has no wait-for-element of its own: its documented async pattern
 * (`onNextClick` + manual `moveNext()`) is for elements the tour itself
 * creates, not for a page that has not finished mounting. Since a tour
 * launched from a notification navigates first, every anchor is potentially
 * late — this is the piece that makes that safe.
 */

/** Attribute marking an element as a tour target. */
export const TOUR_ANCHOR_ATTRIBUTE = 'data-tour'

/** How long to wait for a late anchor before giving up. */
export const DEFAULT_ANCHOR_TIMEOUT_MS = 10_000

export function tourAnchorSelector(anchor: string): string {
  return `[${TOUR_ANCHOR_ATTRIBUTE}="${anchor}"]`
}

/**
 * Resolves once the anchor is in the DOM, or to `undefined` if it never shows
 * up within `timeoutMs`.
 *
 * Resolving rather than throwing is the whole contract: a missing anchor is a
 * bug worth reporting, but never worth failing the user's click over. Callers
 * report the miss and degrade — see `useProductTour`.
 */
export function findAnchor(
  anchor: string,
  timeoutMs: number = DEFAULT_ANCHOR_TIMEOUT_MS
): Promise<HTMLElement | undefined> {
  const selector = tourAnchorSelector(anchor)
  const existing = document.querySelector<HTMLElement>(selector)
  if (existing) return Promise.resolve(existing)

  return new Promise((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout>

    const observer = new MutationObserver(() => {
      const found = document.querySelector<HTMLElement>(selector)
      if (!found) return
      clearTimeout(timeoutId)
      observer.disconnect()
      resolve(found)
    })

    timeoutId = setTimeout(() => {
      observer.disconnect()
      resolve(undefined)
    }, timeoutMs)

    // Anchors can appear anywhere: Element Plus dialogs and dropdowns render
    // with `append-to-body`, outside the page component's subtree entirely.
    observer.observe(document.body, { childList: true, subtree: true })
  })
}

/**
 * Clicks an anchor once it appears, if it appears. Used by `prepare` hooks to
 * drive the UI into the state a later step needs. Deliberately forgiving: a
 * `prepare` step often has to cope with more than one path through the UI
 * (a plain button for some users, a dropdown for others).
 */
export async function clickAnchorIfPresent(
  anchor: string,
  timeoutMs: number = DEFAULT_ANCHOR_TIMEOUT_MS
): Promise<boolean> {
  const element = await findAnchor(anchor, timeoutMs)
  element?.click()
  return element !== undefined
}
